import { cva, type VariantProps } from 'class-variance-authority';

/**
 * ArticleHeroBanner variant styles using Class Variance Authority (CVA)
 *
 * Defines color (background) variants for the hero banner. All styles use
 * design tokens from the HEN Design System. Add new background options here
 * instead of branching in the component.
 *
 * ## Color variants
 * - neutral: Light background (bg-neutral-50), dark text
 * - secondary: Purple background (bg-secondary-700), white text
 */
export const articleHeroBannerVariants = cva('w-full py-md px-page-sides', {
  variants: {
    color: {
      neutral: 'bg-neutral-50',
      secondary: 'bg-secondary-700',
    },
  },
  defaultVariants: {
    color: 'neutral',
  },
});

export type ArticleHeroBannerVariantProps = VariantProps<
  typeof articleHeroBannerVariants
>;

/** Color variant union for the config map */
type ArticleHeroBannerColor = NonNullable<
  ArticleHeroBannerVariantProps['color']
>;

/**
 * Maps each color variant to the props used by child components (Breadcrumb,
 * Button, SectionHeader, Text, AvatarGroup). Add a new entry here when adding
 * a new color variant so the component stays in sync without isSecondary-style branching.
 */
export const articleHeroBannerColorConfig: Record<
  ArticleHeroBannerColor,
  {
    avatarGroupClassName: string;
    breadcrumbColor: 'default' | 'white';
    buttonVariant: 'textOnlyWhite' | 'textOnlySecondary';
    sectionHeaderColor: 'default' | 'white';
    textColor: 'subtext' | 'white';
  }
> = {
  neutral: {
    avatarGroupClassName: '',
    breadcrumbColor: 'default',
    buttonVariant: 'textOnlySecondary',
    sectionHeaderColor: 'default',
    textColor: 'subtext',
  },
  secondary: {
    avatarGroupClassName:
      '[&_[data-slot=avatar]]:ring-secondary-700 [&_[data-slot=avatar-fallback]]:bg-white [&_[data-slot=avatar-fallback]]:text-secondary-700',
    breadcrumbColor: 'white',
    buttonVariant: 'textOnlyWhite',
    sectionHeaderColor: 'white',
    textColor: 'white',
  },
};
